import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { type DateTime } from 'luxon';

import { ApiClient } from './api.client.js';
import { type CreateLog, type Log, type Patient, type UpdateLog } from '../models/index.js';

export const LogsApiKeys = {
  GET_LATEST_LOG: 'GET_LATEST_LOG',
  GET_LOGS_FOR_PATIENT: 'GET_LOGS_FOR_PATIENT',
  CREATE_LOG: 'CREATE_LOG',
  UPDATE_LOG: 'UPDATE_LOG',
  DELETE_LOG: 'DELETE_LOG',
};

export class LogsApi extends ApiClient {
  public static async getLatestLog(patientId: string): Promise<Log> {
    if (!patientId) {
      return Promise.reject(new Error('[getLatestLog] invalid patientId'));
    }
    return this.http
      .get('/logs', {
        params: {
          patientId,
        },
      })
      .then((res) => res.data);
  }

  public static async getLogsFor(
    patientId: string,
    startDate: DateTime,
    endDate: DateTime,
  ): Promise<Patient & { logs: Log[] }> {
    if (!patientId) {
      return Promise.reject(new Error('[getLogsForPatient] Invalid PatientId'));
    }
    return this.http
      .get(`/patients/${patientId}/logs`, {
        params: {
          startDate: startDate.toISO(),
          endDate: endDate.toISO(),
        },
      })
      .then((res) => res.data);
  }
  public static async createLog(log: CreateLog): Promise<Log> {
    return this.http.post(`/logs`, log);
  }
  public static async updateLog(log: UpdateLog): Promise<Log> {
    return this.http.post(`/logs/${log.id}`, log);
  }
  public static async deleteLog(id: string): Promise<void> {
    return this.http.delete(`/logs/${id}`);
  }
}

export function useLatestLog(patientId: string, queryOptions?: UseQueryOptions<Log>) {
  return useQuery<Log>([LogsApiKeys.GET_LATEST_LOG, patientId], () => LogsApi.getLatestLog(patientId), queryOptions);
}

export function useLogsForPatient(
  patientId: string,
  startDate: DateTime,
  endDate: DateTime,
  queryOptions?: UseQueryOptions<Patient & { logs: Log[] }>,
) {
  return useQuery<Patient & { logs: Log[] }>(
    [LogsApiKeys.GET_LOGS_FOR_PATIENT, patientId],
    () => LogsApi.getLogsFor(patientId, startDate, endDate),
    queryOptions,
  );
}
export function useMutateCreateLog(mutationOptions?: UseMutationOptions<Log, unknown, CreateLog>) {
  return useMutation([LogsApiKeys.CREATE_LOG], (log: CreateLog) => LogsApi.createLog(log), mutationOptions);
}
export function useMutateUpdateLog(mutationOptions?: UseMutationOptions<Log, unknown, UpdateLog>) {
  return useMutation([LogsApiKeys.UPDATE_LOG], (log: UpdateLog) => LogsApi.updateLog(log), mutationOptions);
}
export function useMutateDeleteLog(mutationOptions?: UseMutationOptions<void, unknown, string>) {
  return useMutation([LogsApiKeys.DELETE_LOG], (id: string) => LogsApi.deleteLog(id), mutationOptions);
}
